import { Link, useSearch } from "@tanstack/react-router";
import { Icon, type LucideIconName, PageWrapper } from "@/deps/ui-library";
import { PageHeader } from "@/components/PageHeader";
import { cn } from "@/lib/cn";
import { SettingsHubPage } from "./SettingsHubPage";
import { AuditTab } from "./tabs/AuditTab";
import { ChartTab } from "./tabs/ChartTab";
import { CompanyTab } from "./tabs/CompanyTab";
import { NumberingTab } from "./tabs/NumberingTab";
import { ReportTagsTab } from "./tabs/ReportTagsTab";
import { RolesTab } from "./tabs/RolesTab";
import { TaxCodesTab } from "./tabs/TaxCodesTab";
import { UsersTab } from "./tabs/UsersTab";

const TABS: Array<{
  key: string;
  label: string;
  icon: LucideIconName;
  render: () => React.ReactNode;
}> = [
  { key: "company", label: "Company profile", icon: "Building2", render: () => <CompanyTab /> },
  { key: "coa", label: "Chart of accounts", icon: "ListTree", render: () => <ChartTab /> },
  { key: "numbering", label: "Numbering", icon: "Hash", render: () => <NumberingTab /> },
  { key: "tax", label: "Tax codes", icon: "Percent", render: () => <TaxCodesTab /> },
  { key: "tags", label: "Report tags", icon: "BookOpen", render: () => <ReportTagsTab /> },
  { key: "roles", label: "Roles and permissions", icon: "ShieldCheck", render: () => <RolesTab /> },
  { key: "users", label: "Users", icon: "Users", render: () => <UsersTab /> },
  { key: "audit", label: "Audit trail", icon: "History", render: () => <AuditTab /> },
];

/**
 * Settings. With no tab in the URL it is the directory; with one, that page beside a sidebar.
 *
 * `?tab=numbering` is a link somebody can send when they mean "go and look at this". Only the
 * selected page is mounted, because each body loads its own data.
 */
export function SettingsPage() {
  const search = useSearch({ from: "/settings" }) as { tab?: string };
  const active = TABS.find((tab) => tab.key === search.tab);
  if (!active) return <SettingsHubPage />;

  return (
    <PageWrapper spacing="md" className="p-0">
      <PageHeader title={active.label} />
      <div className="flex flex-col gap-4 md:flex-row md:items-start">
        <nav aria-label="Settings sections" className="flex shrink-0 flex-col gap-0.5 md:w-52">
          <SideLink to="/settings" icon="LayoutGrid" label="All settings" />
          {TABS.map((tab) => (
            <SideLink
              key={tab.key}
              to="/settings"
              tab={tab.key}
              icon={tab.icon}
              label={tab.label}
              current={tab.key === active.key}
            />
          ))}
        </nav>
        <div className="min-w-0 flex-1">{active.render()}</div>
      </div>
    </PageWrapper>
  );
}

function SideLink({
  to,
  tab,
  icon,
  label,
  current,
}: {
  to: string;
  tab?: string;
  icon: LucideIconName;
  label: string;
  current?: boolean;
}) {
  return (
    <Link
      to={to as never}
      search={(tab ? { tab } : {}) as never}
      aria-current={current ? "page" : undefined}
      className={cn(
        "flex items-center gap-2 rounded-md px-2 py-1.5 text-sm no-underline",
        current
          ? "bg-accent text-primary border-primary rounded-l-none border-l-2 font-semibold"
          : "text-foreground hover:bg-muted",
      )}
    >
      <Icon name={icon} size="sm" />
      {label}
    </Link>
  );
}
